import { useEffect, useMemo, useState } from "react";
import { Building2, Users, ListOrdered, BookOpen } from "lucide-react";
import { api } from "../api/client";
import type { InstitutionStats } from "../types";

export default function Institutions() {
  const [stats, setStats] = useState<InstitutionStats | null>(null);
  const [jurusanStats, setJurusanStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [institutionData, jurusanData] = await Promise.all([
          api.getInstitutionStats(50),
          api.getJurusanStats(50),
        ]);
        setStats(institutionData);
        setJurusanStats(jurusanData);
      } catch {
        setError("Gagal memuatkan statistik");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const averagePerInstitution = useMemo(() => {
    if (!stats || stats.totalInstitutions === 0) return 0;
    return Math.round(stats.totalStudents / stats.totalInstitutions);
  }, [stats]);

  const genderSummary = useMemo(() => {
    if (!stats) return [];
    return [
      stats.gender.lelaki,
      stats.gender.perempuan,
      stats.gender.tidakDiketahui,
    ];
  }, [stats]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !stats || !jurusanStats) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error || "Tiada data"}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Statistik Institusi</h1>
        <p className="text-slate-600">Maklumat enrolmen pelajar mengikut institusi (Top {stats.topLimit})</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatMini
          title="Jumlah Institusi"
          value={stats.totalInstitutions}
          icon={<Building2 className="w-5 h-5" />}
        />
        <StatMini
          title="Jumlah Pelajar"
          value={stats.totalStudents}
          icon={<Users className="w-5 h-5" />}
        />
        <StatMini
          title={`Pelajar Dalam Top ${stats.topLimit}`}
          value={stats.studentsInTopList}
          icon={<ListOrdered className="w-5 h-5" />}
        />
        <StatMini
          title="Purata / Institusi"
          value={averagePerInstitution}
          icon={<Building2 className="w-5 h-5" />}
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Top {stats.topLimit} Institusi (Enrolmen)</h2>
        {stats.institutions.length === 0 ? (
          <p className="text-slate-500 text-sm">Tiada data institusi.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600 w-16">#</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Institusi</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-slate-600 w-40">Jumlah Pelajar</th>
                </tr>
              </thead>
              <tbody>
                {stats.institutions.map((item, index) => (
                  <tr key={`${item.name}-${index}`} className="border-b border-slate-100">
                    <td className="py-3 px-2 text-slate-500">{index + 1}</td>
                    <td className="py-3 px-2 font-medium text-slate-800">{item.name}</td>
                    <td className="py-3 px-2 text-right font-semibold text-slate-700">{item.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Top {jurusanStats.topLimit} Bidang Pengajian / Jurusan</h2>
        {jurusanStats.jurusan.length === 0 ? (
          <p className="text-slate-500 text-sm">Tiada data jurusan.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600 w-16">#</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Jurusan</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-slate-600 w-40">Jumlah Pelajar</th>
                </tr>
              </thead>
              <tbody>
                {jurusanStats.jurusan.map((item: any, index: number) => (
                  <tr key={`${item.name}-${index}`} className="border-b border-slate-100">
                    <td className="py-3 px-2 text-slate-500">{index + 1}</td>
                    <td className="py-3 px-2 font-medium text-slate-800">{item.name}</td>
                    <td className="py-3 px-2 text-right font-semibold text-slate-700">{item.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Ringkasan Umur (Setiap Umur)</h2>
          {stats.ageGroups.length === 0 ? (
            <p className="text-slate-500 text-sm">Tiada data umur.</p>
          ) : (
            <div className="space-y-3">
              {stats.ageGroups.map((group) => (
                <div key={group.label} className="flex items-center justify-between">
                  <span className="text-slate-700 text-sm">{group.label}</span>
                  <span className="font-semibold text-slate-800">{group.total}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Ringkasan Jantina</h2>
          <div className="space-y-3">
            {genderSummary.map((item) => (
              <div key={item.label} className="flex items-center justify-between">
                <span className="text-slate-700 text-sm">{item.label}</span>
                <span className="font-semibold text-slate-800">{item.total}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Negeri Kelahiran (Semua)</h2>
          {stats.birthStates.length === 0 ? (
            <p className="text-slate-500 text-sm">Tiada data negeri kelahiran.</p>
          ) : (
            <div className="space-y-3">
              {stats.birthStates.map((state) => (
                <div key={state.name} className="flex items-center justify-between">
                  <span className="text-slate-700 text-sm truncate pr-3">{state.name}</span>
                  <span className="font-semibold text-slate-800 shrink-0">{state.total}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatMini({ title, value, icon }: { title: string; value: number; icon: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-slate-500">{title}</p>
        <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
          {icon}
        </div>
      </div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
    </div>
  );
}
